import React, { useId } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  type SelectChangeEvent,
  type FormControlProps,
  type SelectProps,
} from '@mui/material';
import { warehouseSelectFormControlSx, warehouseSelectMenuItemSx, warehouseSelectSx } from './warehouseSelectStyles';

export type WarehouseSelectOption = {
  value: string;
  label: string;
  secondary?: string;
};

export type WarehouseSelectProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options?: WarehouseSelectOption[];
  children?: React.ReactNode;
  disabled?: boolean;
  minWidth?: number | string;
  size?: FormControlProps['size'];
  id?: string;
  emptyOption?: { value: string; label: string };
  formControlSx?: FormControlProps['sx'];
  selectSx?: SelectProps['sx'];
  'data-testid'?: string;
};

export function truncateSelectLabel(value: string, maxLen = 48): string {
  const v = String(value || '').trim();
  if (v.length <= maxLen) return v;
  return `${v.slice(0, maxLen - 1)}…`;
}

export const WarehouseSelectMenuItem: React.FC<{
  value: string;
  primary: string;
  secondary?: string;
  disabled?: boolean;
}> = ({ value, primary, secondary, disabled }) => (
  <MenuItem value={value} disabled={disabled} sx={warehouseSelectMenuItemSx}>
    {secondary ? (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
          {primary}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
          {secondary}
        </Typography>
      </Box>
    ) : (
      <Typography variant="body2" component="span" sx={{ lineHeight: 1.4 }}>
        {primary}
      </Typography>
    )}
  </MenuItem>
);

export const WarehouseSelect: React.FC<WarehouseSelectProps> = ({
  label,
  value,
  onChange,
  options,
  children,
  disabled = false,
  minWidth = 280,
  size = 'small',
  id: idProp,
  emptyOption,
  formControlSx,
  selectSx,
  'data-testid': dataTestId,
}) => {
  const autoId = useId();
  const fieldId = idProp || `warehouse-select-${autoId.replace(/:/g, '')}`;
  const labelId = `${fieldId}-label`;

  const selectedOption = options?.find((o) => o.value === value);
  const displayLabel = selectedOption?.label ?? (value ? truncateSelectLabel(value) : '');

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(String(event.target.value ?? ''));
  };

  return (
    <FormControl
      size={size}
      disabled={disabled}
      sx={{ ...warehouseSelectFormControlSx(minWidth), ...(formControlSx as object) }}
      data-testid={dataTestId}
    >
      <InputLabel id={labelId} htmlFor={fieldId}>
        {label}
      </InputLabel>
      <Select
        id={fieldId}
        labelId={labelId}
        label={label}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        sx={{
          ...warehouseSelectSx,
          ...(selectedOption?.secondary
            ? { '& .MuiSelect-select': { minHeight: 52, py: 1.25 } }
            : {}),
          ...(selectSx as object),
        }}
        renderValue={(selected) => {
          const v = String(selected ?? '');
          if (!v && emptyOption) return emptyOption.label;
          const secondary = selectedOption?.secondary?.trim();
          if (secondary && secondary !== displayLabel) {
            return (
              <Box
                component="span"
                title={`${displayLabel || v} (${secondary})`}
                sx={{ display: 'block', width: '100%', lineHeight: 1.3, py: 0.15 }}
              >
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {displayLabel || v}
                </Typography>
                <Typography
                  variant="caption"
                  component="span"
                  color="text.secondary"
                  sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {secondary}
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              component="span"
              title={displayLabel || v}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', width: '100%' }}
            >
              {displayLabel || v || '—'}
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: { sx: { maxWidth: Math.max(typeof minWidth === 'number' ? minWidth : 320, 320) } },
        }}
      >
        {emptyOption ? (
          <MenuItem value={emptyOption.value}>
            <em>{emptyOption.label}</em>
          </MenuItem>
        ) : null}
        {options
          ? options.map((opt) => (
              <WarehouseSelectMenuItem
                key={opt.value}
                value={opt.value}
                primary={opt.label}
                secondary={opt.secondary}
              />
            ))
          : children}
      </Select>
    </FormControl>
  );
};
